import React from 'react'
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { ProtectedElement, useAuth } from '@praxis/component-auth'
import { useEntitlements } from 'properties-ui-common'
import { appRoutesArray } from './AppRouteItems'
import { resolveLegacyRedirect } from './legacyRedirects'
import { constants } from '../utilities/constants'
import {
  HOME_PATH,
  INDIRECT_ITEM_SEARCH,
  POL_ITEM_EXTERNAL_TAKEOFF_BASE,
  POL_QMS_VENDOR_TAKEOFF_BASE,
  QMS_VENDOR_TAKEOFF_PATH,
} from '../../../MicroFrontEnds/utility/constants'

interface AppRoutesProps {}

export const AppRoutes: React.FC<AppRoutesProps> = () => {
  const { entitlements } = useEntitlements()
  const auth = useAuth()

  const location = useLocation()
  const navigate = useNavigate()

  const isPolUser = entitlements?.pol?.read
  const isPolQmsUser = entitlements?.qms?.pol
  const isPolNriAllowed = entitlements?.nri?.pol

  const isBaseQMSVendorTakeoffPath = window.location.hostname.includes(
    POL_QMS_VENDOR_TAKEOFF_BASE,
  )

  const isBaseItemExternalTakeoffPath = window.location.hostname.includes(
    POL_ITEM_EXTERNAL_TAKEOFF_BASE,
  )

  /**
   * Check whether the current URL is an old route.
   *
   * This is calculated before the authorization logic so that
   * authorization does not treat the old URL as an invalid route.
   */
  const legacyRedirectPath = React.useMemo(
    () => resolveLegacyRedirect(location.pathname),
    [location.pathname],
  )

  // Redirect unauthorized external users.
  React.useEffect(() => {
    /**
     * Do not run authorization checks against an old URL.
     * The Navigate component below will redirect it first.
     */
    if (legacyRedirectPath) {
      return
    }

    if (
      !(isPolUser || isPolQmsUser || isPolNriAllowed) ||
      location.pathname === constants.unAuthorizedRoute
    ) {
      return
    }

    const routes = appRoutesArray()

    const matchingRoute = routes.find((route) =>
      matchPath(route.path, location.pathname),
    )

    const isVendorTakeOffPath = window.location.hostname.includes(
      POL_QMS_VENDOR_TAKEOFF_BASE,
    )

    const isItemTakeOffPath = window.location.hostname.includes(
      POL_ITEM_EXTERNAL_TAKEOFF_BASE,
    )

    const redirectUnauthorized = () => {
      navigate(constants.unAuthorizedRoute, {
        replace: true,
        state: {
          unauthorizedRoute: location.pathname,
        },
      })
    }

    const redirectToVendorTakeoff = () => {
      navigate(QMS_VENDOR_TAKEOFF_PATH, {
        replace: true,
      })
    }

    const redirectToItemTakeoff = () => {
      navigate(INDIRECT_ITEM_SEARCH, {
        replace: true,
      })
    }

    /**
     * Find the most specific matching route or any parent path
     * that matches.
     *
     * This allows POL access to be managed inside the MFE instead
     * of listing every POL route in the container.
     */
    const matchingPolRoute = routes.find(
      (route) =>
        location.pathname === route.path ||
        (route.isExact && location.pathname.startsWith(route.path)),
    )

    const isOnHomePath = location.pathname === HOME_PATH

    // Only POL user.
    if (isPolUser && !isPolQmsUser && !isPolNriAllowed) {
      if (!matchingPolRoute?.isPolAllowed) {
        redirectUnauthorized()
      }

      return
    }

    // Both POL and QMS POL users.
    if (isPolUser && isPolQmsUser && isBaseQMSVendorTakeoffPath) {
      const isAllowed =
        matchingPolRoute?.isPolAllowed ||
        matchingRoute?.isPolQmsAllowed ||
        isVendorTakeOffPath

      if (isOnHomePath) {
        redirectToVendorTakeoff()
        return
      }

      if (!isAllowed) {
        redirectUnauthorized()
      }

      return
    }

    // Only QMS POL user.
    if (isPolQmsUser && !isPolUser && isBaseQMSVendorTakeoffPath) {
      const isAllowed = matchingRoute?.isPolQmsAllowed || isVendorTakeOffPath

      if (isOnHomePath) {
        redirectToVendorTakeoff()
        return
      }

      if (!isAllowed) {
        redirectUnauthorized()
      }

      return
    }

    // Both POL and NRI POL user.
    if (isPolUser && isPolNriAllowed && isBaseItemExternalTakeoffPath) {
      const isNriAllowed =
        matchingRoute?.isPolNriAllowed ||
        isItemTakeOffPath ||
        matchingPolRoute?.isPolAllowed

      if (isOnHomePath) {
        redirectToVendorTakeoff()
        return
      }

      if (!isNriAllowed) {
        redirectUnauthorized()
      }

      return
    }

    // Only NRI POL user.
    const isNriItemSearchHost =
      /^tcinitemsearch\.(perf\.)?partnersonline\.com$/.test(
        window.location.hostname,
      )

    if (isPolNriAllowed && isNriItemSearchHost) {
      if (isOnHomePath) {
        redirectToItemTakeoff()
        return
      }

      if (!matchingRoute?.isPolNriAllowed) {
        redirectUnauthorized()
      }
    }
  }, [
    location,
    legacyRedirectPath,
    isPolUser,
    isPolQmsUser,
    isPolNriAllowed,
    isBaseItemExternalTakeoffPath,
    isBaseQMSVendorTakeoffPath,
    navigate,
    auth?.session?.userInfo,
  ])

  /**
   * Redirect old routes before rendering or authorizing the
   * regular application routes.
   *
   * Existing query parameters and hashes are preserved.
   */
  if (legacyRedirectPath) {
    return (
      <Navigate
        to={{
          pathname: legacyRedirectPath,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    )
  }

  return (
    <Routes>
      {appRoutesArray().map((route: any) => {
        const Element = route.component

        const routePath = route.isExact ? `${route.path}/*` : route.path

        if (!route.protected) {
          return (
            <Route key={route.path} path={routePath} element={<Element />} />
          )
        }

        return (
          <Route
            key={route.path}
            path={routePath}
            element={
              <ProtectedElement
                allowed={route.allowed}
                unauthorizedRoute={constants.unAuthorizedRoute}
              >
                <Element />
              </ProtectedElement>
            }
          />
        )
      })}
    </Routes>
  )
}
